import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordWithToken } from '../api/auth';
import { getApiErrorMessage } from '../api/http';
import { toEnglishUserMessage } from '../lib/messageI18n';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing or invalid reset token');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await resetPasswordWithToken({ token, password, confirmPassword });
      if (res.success) {
        navigate('/login', {
          replace: true,
          state: {
            flash: toEnglishUserMessage(res.message || 'Password changed. You can sign in.'),
          },
        });
        return;
      }
      setError(toEnglishUserMessage(res.message || 'Reset failed'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Reset failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: 'linear-gradient(180deg,#f8f9fe,#fff)' }}>
      <nav className="navbar navbar-dark public-hero">
        <div className="container">
          <Link className="navbar-brand" to="/">
            M3UStudio
          </Link>
          <Link className="btn btn-light btn-sm rounded-pill" to="/login">
            Sign in
          </Link>
        </div>
      </nav>
      <div className="flex-grow-1 d-flex align-items-center py-5">
        <div className="container" style={{ maxWidth: 440 }}>
          <div className="card shadow border-0 rounded-4 overflow-hidden">
            <div style={{ height: 4, background: 'linear-gradient(87deg,#5e72e4,#825ee4)' }} />
            <div className="card-body p-4">
              <p className="small text-uppercase text-primary fw-semibold mb-1">M3UStudio</p>
              <h1 className="h4 fw-bold mb-3">Set new password</h1>
              {!token ? (
                <>
                  <div className="alert alert-warning py-2 small">
                    This reset link is invalid. Request a new one from the sign-in page.
                  </div>
                  <p className="small text-center mt-3 mb-0">
                    <Link to="/forgot-password">Request reset link</Link>
                  </p>
                </>
              ) : (
                <>
                  {error && <div className="alert alert-danger py-2 small">{error}</div>}
                  <form onSubmit={onSubmit}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="rp-p">
                        New password
                      </label>
                      <input
                        id="rp-p"
                        type="password"
                        className="form-control form-control-lg"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={6}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label" htmlFor="rp-c">
                        Confirm new password
                      </label>
                      <input
                        id="rp-c"
                        type="password"
                        className="form-control form-control-lg"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={6}
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-primary w-100 rounded-pill py-2" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving…
                        </>
                      ) : (
                        'Update password'
                      )}
                    </button>
                  </form>
                </>
              )}
              <p className="small text-center mt-3 mb-0">
                <Link to="/login">Back to sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
